import React from 'react';
import {
  complianceTypeLabel,
  findProjectMember,
  getIssueComplianceType,
  getPrimaryRequirementRef,
  memberAvatarColor,
  memberInitials,
  statusBadgeClass,
  statusLabel,
} from './complianceIssuePresenters';
import { DocEvidencePill } from './ComplianceReferencePills.jsx';
import { RequirementRefCell } from './RequirementHoverCard.jsx';
import useResizableTableColumns from './useResizableTableColumns.js';

const TABLE_COLUMNS = [
  { key: 'id', label: 'ID', defaultWidth: 120, minWidth: 72 },
  { key: 'topic', label: 'Topic', defaultWidth: 220, minWidth: 100 },
  { key: 'requirement', label: 'Requirement', defaultWidth: 140, minWidth: 88 },
  { key: 'evidence', label: 'Evidence Doc', defaultWidth: 140, minWidth: 88 },
  { key: 'status', label: 'Status', defaultWidth: 110, minWidth: 88 },
  { key: 'complianceType', label: 'Compliance Type', defaultWidth: 140, minWidth: 100 },
  { key: 'assignee', label: 'Assigned To', defaultWidth: 130, minWidth: 80 },
  { key: 'created', label: 'Created', defaultWidth: 150, minWidth: 96 },
];

const COLUMN_WIDTHS_STORAGE_KEY = 'compliance-column-widths';
const LEGACY_COLUMN_WIDTHS_STORAGE_KEY = 'compliance-may7-column-widths';

export default function ComplianceIssuesTable({
  issues,
  selectedIssueId,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,
  onSelectIssue,
  projectMembers = [],
  displayAssigneeName,
  onOpenEvidenceDoc,
  emptyMessage = 'No compliance issues found.',
}) {
  const selectedSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const visibleIds = issues.map((i) => i.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedSet.has(id));
  const { getColWidth, startResize, resizingKey } = useResizableTableColumns(
    TABLE_COLUMNS,
    COLUMN_WIDTHS_STORAGE_KEY,
    LEGACY_COLUMN_WIDTHS_STORAGE_KEY
  );

  const tableMinWidth = TABLE_COLUMNS.reduce((sum, c) => sum + getColWidth(c.key), 0);

  return (
    <div className="table-container">
      <table className="issues-table issues-table--resizable issues-table--selectable" style={{ minWidth: tableMinWidth + 44 }}>
        <colgroup>
          <col className="col-select" style={{ width: 44 }} />
          {TABLE_COLUMNS.map((col) => (
            <col key={col.key} style={{ width: getColWidth(col.key) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="th-select" data-column="select">
              <input
                type="checkbox"
                className="issues-table__checkbox"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                }}
                onChange={() => onToggleSelectAll && onToggleSelectAll()}
                onClick={(e) => e.stopPropagation()}
                aria-label={allVisibleSelected ? 'Deselect all issues' : 'Select all issues'}
              />
            </th>
            {TABLE_COLUMNS.map((col) => (
              <th key={col.key} data-column={col.key} style={{ width: getColWidth(col.key) }}>
                <span className="th-inner">{col.label}</span>
                <span
                  className={`resize-handle${resizingKey === col.key ? ' resizing' : ''}`}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${col.label} column`}
                  onMouseDown={(e) => startResize(col.key, e)}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => {
            const meta = issue.evidenceDocMeta;
            const evidenceTitle = (issue.evidenceDoc || meta?.title || '').trim();
            const hasEvidenceKey =
              !!meta?.originalPdfKey ||
              meta?.references?.some((r) => r.originalPdfKey);
            const requirementRef = getPrimaryRequirementRef(issue);
            const requirementLabel = (
              requirementRef?.label || requirementRef?.value || ''
            ).trim();

            const isChecked = selectedSet.has(issue.id);

            return (
              <tr
                key={issue.id}
                id={`row-${issue.id}`}
                className={[
                  selectedIssueId === issue.id ? 'active' : '',
                  isChecked ? 'selected-for-delete' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onSelectIssue(issue)}
              >
                <td className="td-select" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="issues-table__checkbox"
                    checked={isChecked}
                    onChange={() => onToggleSelect && onToggleSelect(issue.id)}
                    aria-label={`Select ${issue.ncCode || issue.display_id || 'issue'}`}
                  />
                </td>
                <td className="td-id">{issue.ncCode || issue.display_id || '—'}</td>
                <td className="td-topic">{issue.topic || issue.identifiedIssue || '—'}</td>
                <td className="td-requirement">
                  <div className="td-requirement-inner">
                    {requirementLabel ? (
                      <RequirementRefCell issue={issue} requirementRef={requirementRef} />
                    ) : (
                      <span className="td-ref-pill">—</span>
                    )}
                  </div>
                </td>
                <td className="td-evidence">
                  <div className="td-evidence-inner">
                    <DocEvidencePill
                      title={evidenceTitle || '—'}
                      pageNumber={meta?.pageNumber}
                      clickable={!!hasEvidenceKey && !!onOpenEvidenceDoc}
                      onClick={() => onOpenEvidenceDoc(issue)}
                    />
                  </div>
                </td>
                <td className="td-status">
                  <span className={statusBadgeClass(issue.status)}>
                    {statusLabel(issue.status)}
                  </span>
                </td>
                <td>
                  <span className="type-label">{complianceTypeLabel(getIssueComplianceType(issue))}</span>
                </td>
                <td className="td-assignee">
                  {(() => {
                    const member = findProjectMember(projectMembers, issue.assignedTo);
                    const name = member?.name || displayAssigneeName(issue.assignedTo);
                    if (!name) return <span className="td-assignee-empty">—</span>;
                    const initials = memberInitials(name);
                    const color = memberAvatarColor(issue.assignedTo || name);
                    return (
                      <span className="td-assignee-avatars" title={name}>
                        <span className="avatar-circle" style={{ background: color }}>
                          {initials}
                        </span>
                      </span>
                    );
                  })()}
                </td>
                <td className="td-date">{issue.formattedCreatedAt || '—'}</td>
              </tr>
            );
          })}
          {issues.length === 0 && (
            <tr>
              <td colSpan={TABLE_COLUMNS.length + 1} style={{ padding: 24, color: '#666', textAlign: 'center' }}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
